import { type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useKioskMode } from '../hooks/useKiosk';
import { NarrativeView } from './NarrativeView';
import { SummaryImageView } from './SummaryImageView';
import { SpeechProfileView } from './SpeechProfileView';
import { KeywordsView } from './KeywordsView';
import { SummaryTextView } from './SummaryTextView';
import { TakeawayView } from './TakeawayView';
import { ThemesView } from './ThemesView';
import c from 'classnames';
import { useDidMount } from '../utils/useDidMount';
import { VerbiagesView } from './VerbiagesView';
import { SentimentView } from './SentimentView';
import { PromisesView } from './PromisesView';
import { StatisticsView } from './StatisticsView';
import { SummaryStatsView } from './SummaryStatsView';
import { isRecording } from '../utils/elapsedTime';
import { KlartextEventConfigContext } from '../providers/klartextEventConfig';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';
import '../styles/swiper.css';
import { useKpiCollection } from '../hooks/useKpiCollection';
import { SummaryKpiPoint } from '@configTypes/kpiSummary';
import { GlobalStateProvider } from '../providers/globalState';
import { AttacksView } from './AttacksView';
import { FinalStatementImageKpiPoint } from '@configTypes/kpiImage';

type CarouselSlide = {
  headerTitle: string;
  component: ReactNode;
};

const slides: CarouselSlide[] = [
  { headerTitle: 'Themes', component: <ThemesView /> },
  { headerTitle: 'Takeaways', component: <TakeawayView /> },
  {
    headerTitle: 'Speech profile',
    component: <SpeechProfileView />,
  },
  { headerTitle: 'Attacks', component: <AttacksView /> },
  { headerTitle: 'Platitudes', component: <VerbiagesView /> },
  { headerTitle: 'Sentiment', component: <SentimentView /> },
  { headerTitle: 'Statistics', component: <StatisticsView /> },
];

export const AnalysisCarousel = () => {
  const speechConfig = useContext(KlartextEventConfigContext);
  const mounted = useDidMount();
  const { isKioskMode } = useKioskMode();
  const autoplay = isKioskMode;

  const [activeSlides, setActiveSlides] = useState<CarouselSlide[]>(slides);

  const [summary, setSummary] = useState<string | null>(null);
  const [summaryKpi] = useKpiCollection<SummaryKpiPoint[]>({
    eventId: speechConfig?.eventId ?? null,
    kpiName: 'Summary',
    initialState: [] as SummaryKpiPoint[],
  });

  const [imageKpiPoints] = useKpiCollection<FinalStatementImageKpiPoint[]>({
    eventId: speechConfig?.eventId ?? null,
    kpiName: 'SummaryImg',
    initialState: [] as FinalStatementImageKpiPoint[],
  });

  const summaryImageRepublican = useMemo(() => {
    return imageKpiPoints
      .filter(imageKpi => imageKpi.speaker === speechConfig?.frontendSpecificConfig.participants.republican.name)
      .sort((a, b) => b.date - a.date)[0];
  }, [imageKpiPoints]);

  const summaryImageDemocrat = useMemo(() => {
    return imageKpiPoints
      .filter(imageKpi => imageKpi.speaker === speechConfig?.frontendSpecificConfig.participants.democrat.name)
      .sort((a, b) => b.date - a.date)[0];
  }, [imageKpiPoints]);

  useEffect(() => {
    console.log('summaryImageRepublican', summaryImageRepublican);
    console.log('summaryImageDemocrat', summaryImageDemocrat);
  }, [summaryImageRepublican, summaryImageDemocrat]);

  useEffect(() => {
    if (summaryKpi.length === 0) return;
    const summary = summaryKpi[summaryKpi.length - 1];
    setSummary(summary.value);
  }, [summaryKpi]);

  const progressCircle = useRef<SVGSVGElement | null>(null);
  const onAutoplayTimeLeft = (s: SwiperRef['swiper'], time: number, progress: number) => {
    if (progressCircle.current === null) return;
    progressCircle.current.style.setProperty('--progress', `${1 - progress}`);
  };

  const modules = autoplay ? [Pagination, Autoplay] : [Pagination];

  useEffect(() => {
    if (summaryImageRepublican && summaryImageDemocrat) {
      // inject the visualization slides after the takeaways slide
      setActiveSlides([
        ...slides.slice(0, 2),
        {
          headerTitle: 'Visualization',
          component: <SummaryImageView imageKpiPoint={summaryImageDemocrat} />,
        } as CarouselSlide,
        {
          headerTitle: 'Visualization',
          component: <SummaryImageView imageKpiPoint={summaryImageRepublican} />,
        } as CarouselSlide,
        ...slides.slice(2),
      ]);
    }
  }, [speechConfig, summary, imageKpiPoints]);

  return (
    <GlobalStateProvider>
      <div className="flex h-full">
        <Swiper
          modules={modules}
          slidesPerView={1}
          pagination={{ clickable: true }}
          autoplay={autoplay ? { delay: 10000, pauseOnMouseEnter: true } : false}
          onAutoplayTimeLeft={onAutoplayTimeLeft}>
          {activeSlides.map((slide, i) => (
            <SwiperSlide key={slide.headerTitle + i}>
              <div className={'flex h-full flex-col'}>
                <div className="flex h-20 items-center px-2.5 text-eidraBlack">
                  <h2 className="mt-1 text-[2.5rem] font-bold leading-none">{slide.headerTitle}</h2>
                </div>

                <div
                  className={c(
                    'relative mt-[3.9rem] flex-auto transition-opacity duration-[1.5s]',
                    mounted ? 'opacity-100' : 'opacity-0',
                    isRecording(speechConfig) && 'mt-[5rem]',
                  )}>
                  <div className="pointer-events-auto absolute inset-0 mb-12 overflow-y-auto from-eidraCream to-transparent px-2.5 pb-10 after:fixed after:bottom-0 after:mb-12 after:block after:h-10 after:w-full after:bg-gradient-to-t">
                    {slide.component}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
          <div className="autoplay-progress" slot="container-end">
            <svg viewBox="0 0 48 48" ref={progressCircle}>
              <circle cx="24" cy="24" r="20"></circle>
            </svg>
          </div>
        </Swiper>
      </div>
    </GlobalStateProvider>
  );
};
